/**
 * Formats a date into a compact relative time string
 * Examples: "just now", "5m", "3h", "2mo", "1y"
 * @param {Date|string|number} date - The date to format
 * @returns {string} Compact time string
 */
export function formatTimeCompact(date) {
    const now = new Date();
    const targetDate = new Date(date);
    const diffInSeconds = Math.floor((now - targetDate) / 1000);
    
    // Just now (less than 1 minute)
    if (diffInSeconds < 60) {
        return 'just now';
    }
    
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    
    // Minutes (less than 1 hour)
    if (diffInMinutes < 60) {
        return `${diffInMinutes}m`;
    }
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    
    // Hours (less than 1 day)
    if (diffInHours < 24) {
        return `${diffInHours}h`;
    }
    
    const diffInDays = Math.floor(diffInHours / 24);
    
    // Days (less than 1 month ~30 days)
    if (diffInDays < 30) {
        return `${diffInDays}d`;
    }
    
    const diffInMonths = Math.floor(diffInDays / 30);
    
    // Months (less than 1 year)
    if (diffInMonths < 12) {
        return `${diffInMonths}mo`;
    }
    
    // Years
    const diffInYears = Math.floor(diffInMonths / 12);
    return `${diffInYears}y`;
}

/**
 * Formats a date into a compact time string with more precision
 * Shows exact numbers instead of rounding
 * @param {Date|string|number} date - The date to format
 * @returns {string} Compact time string with exact numbers
 */
export function formatTimeCompactExact(date) {
    const now = new Date();
    const targetDate = new Date(date);
    
    // Handle future dates (shouldn't happen, but just in case)
    if (targetDate > now) {
        return 'just now';
    }
    
    const diffInMilliseconds = now - targetDate;
    const diffInSeconds = Math.floor(diffInMilliseconds / 1000);
    
    // Just now (less than 1 minute)
    if (diffInSeconds < 60) {
        return 'just now';
    }
    
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    
    // Minutes (less than 1 hour)
    if (diffInMinutes < 60) {
        return `${diffInMinutes}m`;
    }
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    
    // Hours (less than 1 day)
    if (diffInHours < 24) {
        return `${diffInHours}h`;
    }
    
    const diffInDays = Math.floor(diffInHours / 24);
    
    // Days (less than 1 month ~30 days)
    if (diffInDays < 30) {
        return `${diffInDays}d`;
    }
    
    const diffInMonths = Math.floor(diffInDays / 30);
    
    // Months (less than 1 year)
    if (diffInMonths < 12) {
        return `${diffInMonths}mo`;
    }
    
    // Years
    const diffInYears = Math.floor(diffInDays / 365);
    return `${diffInYears}y`;
}

/**
 * Formats a date into a compact time string with seconds precision
 * Shows seconds for very recent items
 * @param {Date|string|number} date - The date to format
 * @returns {string} Compact time string with seconds
 */
export function formatTimeCompactWithSeconds(date) {
    const now = new Date();
    const targetDate = new Date(date);
    const diffInSeconds = Math.floor((now - targetDate) / 1000);
    
    // Just now (less than 5 seconds)
    if (diffInSeconds < 5) {
        return 'just now';
    }
    
    // Seconds (less than 1 minute)
    if (diffInSeconds < 60) {
        return `${diffInSeconds}s`;
    }
    
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    
    // Minutes (less than 1 hour)
    if (diffInMinutes < 60) {
        return `${diffInMinutes}m`;
    }
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    
    // Hours (less than 1 day)
    if (diffInHours < 24) {
        return `${diffInHours}h`;
    }
    
    const diffInDays = Math.floor(diffInHours / 24);
    
    // Days (less than 1 month ~30 days)
    if (diffInDays < 30) {
        return `${diffInDays}d`;
    }
    
    const diffInMonths = Math.floor(diffInDays / 30);
    
    // Months (less than 1 year)
    if (diffInMonths < 12) {
        return `${diffInMonths}mo`;
    }
    
    // Years
    const diffInYears = Math.floor(diffInDays / 365);
    return `${diffInYears}y`;
}